import { computed, inject, Injectable, signal, OnDestroy } from '@angular/core';
import { IFriend, IFriendsResponse } from "./model/friends.model";
import { BehaviorSubject, exhaustMap, finalize, of, Subscription, tap } from "rxjs";
import { FriendsCrudService } from "./service/friends-crud.service";
import { FacetModel } from "../my-history/facet/facet.model";
import { IStory } from "../my-history/story/story";
import { FacetsResponse } from "../my-history/facet/facets-response";
import { Facet, FacetAliasId } from "../my-history/facet/facet";
import { FacetService } from "../my-history/facet/facet.service";

@Injectable({
  providedIn: 'root',
})
export class FriendsService implements OnDestroy {
  friendsCrudService = inject(FriendsCrudService);
  friendsList = signal<IFriend[]>([]);

  private subscriptions = new Subscription();
  private isFriendsFetching = false;
  private allFriendsLoaded = false;

  // Додаткові змінні для пагінації історій
  private isStoriesFetching = false;
  private allStoriesLoaded = false;
  private currentStoriesPage = 0;
  private readonly storiesPageSize = 20;

  // Додаткові змінні для пагінації історій за фасетом
  private isFacetStoriesFetching = false;
  private allFacetStoriesLoaded = false;
  private currentFacetStoriesPage = 0;
  private readonly facetStoriesPageSize = 50;

  // BehaviorSubjects для реактивного оновлення
  private currentFriendSubject = new BehaviorSubject<IFriend | null>(null);
  currentFriend$ = this.currentFriendSubject.asObservable();

  private currentFriendFacetsSubject = new BehaviorSubject<FacetModel[] | null>(null);
  currentFriendFacets$ = this.currentFriendFacetsSubject.asObservable();

  private currentStoriesOfFacetSubject = new BehaviorSubject<IStory[] | null>(null);
  currentStoriesOfFacet$ = this.currentStoriesOfFacetSubject.asObservable();

  // Додаємо новий subject для загальної кількості історій за фасетом
  private storiesOfFacetTotalSubject = new BehaviorSubject<number>(0);
  storiesOfFacetTotal$ = this.storiesOfFacetTotalSubject.asObservable();

  // Додаємо subject для стану завантаження історій за фасетом
  private facetStoriesLoadingSubject = new BehaviorSubject<boolean>(false);
  facetStoriesLoading$ = this.facetStoriesLoadingSubject.asObservable();

  private currentFriendIdSubject = new BehaviorSubject<number | null>(null);
  currentFriendId$ = this.currentFriendIdSubject.asObservable();

  private currentFriendChaptersSubject = new BehaviorSubject<FacetModel[] | null>(null);
  currentFriendChapters$ = this.currentFriendChaptersSubject.asObservable();

  private currentFriendChapterSubject = new BehaviorSubject<FacetModel | null>(null);
  currentFriendChapter$ = this.currentFriendChapterSubject.asObservable();

  private currentFriendFacetSubject = new BehaviorSubject<FacetModel | null>(null);
  currentFriendFacet$ = this.currentFriendFacetSubject.asObservable();

  private allFriendStoriesSubject = new BehaviorSubject<IStory[]>([]);
  allFriendStories$ = this.allFriendStoriesSubject.asObservable();

  private allFriendStoriesTotalSubject = new BehaviorSubject<number>(0);
  allFriendStoriesTotal$ = this.allFriendStoriesTotalSubject.asObservable();

  // Новий BehaviorSubject для стану завантаження історій
  private storiesLoadingSubject = new BehaviorSubject<boolean>(false);
  storiesLoading$ = this.storiesLoadingSubject.asObservable();

  selectedFacetTypeId = signal<number>(FacetAliasId.chapter);

  private selectedFacetTypeIdSubject = new BehaviorSubject<number>(FacetAliasId.chapter);
  selectedFacetTypeId$ = this.selectedFacetTypeIdSubject.asObservable();

  private facetService = inject(FacetService);

  selectedFacetType = computed(() =>
    this.facetService.friendFacetTypes().find((type) => type.id === this.selectedFacetTypeId()),
  );

  protected friendsTotal = signal(0);

  constructor() {}

  setCurrentFriendChapter(chapter: FacetModel | null) {
    this.currentFriendChapterSubject.next(chapter);
  }

  setCurrentFriend(friend: IFriend | null) {
    this.currentFriendSubject.next(friend);
  }

  setSelectedFacetType(facet: Facet | null) {
    const facetTypeId = facet ? facet.id : FacetAliasId.chapter;
    this.selectedFacetTypeId.set(facetTypeId);
    this.selectedFacetTypeIdSubject.next(facetTypeId);
  }

  setCurrentFriendId(id: number | null) {
    this.currentFriendIdSubject.next(id);
  }

  // Повне очищення всіх даних при переході між друзями
  resetAllUserData() {
    this.currentFriendSubject.next(null);
    this.currentFriendChapterSubject.next(null);
    this.currentFriendFacetsSubject.next(null);
    this.currentFriendChaptersSubject.next(null);
    this.currentStoriesOfFacetSubject.next(null);
    this.storiesOfFacetTotalSubject.next(0);
    this.currentFriendFacetSubject.next(null);
    this.currentFriendIdSubject.next(null);
    this.allFriendStoriesSubject.next([]);
    this.allFriendStoriesTotalSubject.next(0);
    this.selectedFacetTypeId.set(FacetAliasId.chapter);
    this.selectedFacetTypeIdSubject.next(FacetAliasId.chapter);

    // Скидаємо пагінацію історій
    this.resetStoriesPagination();
    this.resetFacetStoriesPagination();
  }

  // Новий метод для скидання пагінації історій
  private resetStoriesPagination() {
    this.currentStoriesPage = 0;
    this.allStoriesLoaded = false;
    this.isStoriesFetching = false;
    this.storiesLoadingSubject.next(false);
  }

  // Новий метод для скидання пагінації історій за фасетом
  private resetFacetStoriesPagination() {
    this.currentFacetStoriesPage = 0;
    this.allFacetStoriesLoaded = false;
    this.isFacetStoriesFetching = false;
    this.facetStoriesLoadingSubject.next(false);
  }

  setFriendChapters(chapters: FacetModel[]) {
    this.currentFriendChaptersSubject.next(chapters);
  }

  setFriendFacets(facets: FacetModel[]) {
    this.currentFriendFacetsSubject.next(facets);
  }

  setAllFriendsStories(stories: IStory[]) {
    this.allFriendStoriesSubject.next(stories);
  }

  setAllFriendsStoriesTotal(total: number) {
    this.allFriendStoriesTotalSubject.next(total);
  }

  setFriendFacet(facet: FacetModel | null) {
    this.currentFriendFacetSubject.next(facet);
  }

  setFriendStoriesOfFacet(storiesOfFacet: IStory[]) {
    this.currentStoriesOfFacetSubject.next(storiesOfFacet);
  }

  setStoriesOfFacetTotal(total: number) {
    this.storiesOfFacetTotalSubject.next(total);
  }

  getFacets(friendId: number, start: string, end?: string): void {
    this.currentFriendFacetsSubject.next(null);

    this.friendsCrudService
      .getChapterFacets(friendId, start, end)
      .subscribe({
        next: (friendChapterFacets) => {
          this.setFriendFacets(friendChapterFacets);
        },
        error: (error) => {
          console.error('Error loading facets:', error);
          this.setFriendFacets([]);
        }
      });
  }

  // Оновлений метод для завантаження історій з пагінацією
  getAllFriendStories(friendId: number, start: string, end: string, resetPagination = true): void {
    if (resetPagination) {
      // Початкове завантаження - очищаємо попередні історії та скидаємо пагінацію
      this.resetStoriesPagination();
      this.allFriendStoriesSubject.next([]);
      this.allFriendStoriesTotalSubject.next(0);
    }

    if (this.isStoriesFetching || this.allStoriesLoaded) {
      return;
    }

    this.isStoriesFetching = true;
    this.storiesLoadingSubject.next(true);

    this.friendsCrudService
      .getAllFriendStories(friendId, start, end, this.currentStoriesPage, this.storiesPageSize)
      .pipe(
        finalize(() => {
          this.isStoriesFetching = false;
          this.storiesLoadingSubject.next(false);
        })
      )
      .subscribe({
        next: (friendsStories: FacetsResponse<IStory>) => {
          if (friendsStories.data && friendsStories.data.length) {
            if (this.currentStoriesPage === 0) {
              // Перша сторінка - замінюємо історії
              this.setAllFriendsStories(friendsStories.data);
            } else {
              // Наступні сторінки - додаємо до існуючих
              const currentStories = this.allFriendStoriesSubject.value;
              const updatedStories = [...currentStories, ...friendsStories.data];
              this.setAllFriendsStories(updatedStories);
            }

            this.allFriendStoriesTotalSubject.next(friendsStories.total);

            // Перевіряємо, чи є ще історії для завантаження
            this.allStoriesLoaded = friendsStories.data.length < this.storiesPageSize;
            this.currentStoriesPage++;
          } else {
            // Немає більше історій
            this.allStoriesLoaded = true;
            if (this.currentStoriesPage === 0) {
              this.setAllFriendsStories([]);
            }
          }
        },
        error: (error) => {
          console.error('Error loading stories:', error);
          if (this.currentStoriesPage === 0) {
            this.setAllFriendsStories([]);
          }
          this.allStoriesLoaded = true;
        }
      });
  }

  // Новий метод для завантаження наступної сторінки історій
  loadMoreFriendStories(friendId: number, start: string, end: string): void {
    if (!this.isStoriesFetching && !this.allStoriesLoaded) {
      this.getAllFriendStories(friendId, start, end, false);
    }
  }

  // Оновлений метод getStoriesForFacet з підтримкою пагінації
  getStoriesForFacet(
    friendId: number,
    facetId: string,
    start: string,
    end?: string,
    limit: number = 50,
    offset: number = 0,
    resetPagination: boolean = true
  ): void {
    if (resetPagination) {
      // Початкове завантаження - очищаємо попередні історії та скидаємо пагінацію
      this.resetFacetStoriesPagination();
      this.currentStoriesOfFacetSubject.next(null);
      this.storiesOfFacetTotalSubject.next(0);
    }

    if (this.isFacetStoriesFetching || this.allFacetStoriesLoaded) {
      return;
    }

    this.isFacetStoriesFetching = true;
    this.facetStoriesLoadingSubject.next(true);

    // Розраховуємо skip на основі поточної сторінки
    const skip = this.currentFacetStoriesPage * this.facetStoriesPageSize;

    this.friendsCrudService
      .getStoriesForFacetWithPagination(friendId, facetId, start, end, this.facetStoriesPageSize, skip)
      .pipe(
        finalize(() => {
          this.isFacetStoriesFetching = false;
          this.facetStoriesLoadingSubject.next(false);
        })
      )
      .subscribe({
        next: (friendChapterFacets: FacetsResponse<IStory>) => {
          if (friendChapterFacets.data && friendChapterFacets.data.length > 0) {
            if (this.currentFacetStoriesPage === 0) {
              // Перша сторінка - замінюємо історії
              this.setFriendStoriesOfFacet(friendChapterFacets.data);
            } else {
              // Наступні сторінки - додаємо до існуючих
              const currentStories = this.currentStoriesOfFacetSubject.value || [];
              const updatedStories = [...currentStories, ...friendChapterFacets.data];
              this.setFriendStoriesOfFacet(updatedStories);
            }

            this.setStoriesOfFacetTotal(friendChapterFacets.total);

            // Перевіряємо, чи є ще історії для завантаження
            this.allFacetStoriesLoaded = friendChapterFacets.data.length < this.facetStoriesPageSize;
            this.currentFacetStoriesPage++;
          } else {
            // Немає більше історій
            this.allFacetStoriesLoaded = true;
            if (this.currentFacetStoriesPage === 0) {
              this.setFriendStoriesOfFacet([]);
            }
          }
        },
        error: (error) => {
          console.error('Error loading stories for facet:', error);
          if (this.currentFacetStoriesPage === 0) {
            this.setFriendStoriesOfFacet([]);
          }
          this.allFacetStoriesLoaded = true;
        }
      });
  }

  // Новий метод для завантаження наступної сторінки історій за фасетом
  loadMoreStoriesForFacet(friendId: number, facetId: string, start: string, end?: string): void {
    if (!this.isFacetStoriesFetching && !this.allFacetStoriesLoaded) {
      this.getStoriesForFacet(friendId, facetId, start, end, this.facetStoriesPageSize, 0, false);
    }
  }

  // Геттер для перевірки стану завантаження
  get isLoadingStories(): boolean {
    return this.isStoriesFetching;
  }

  // Геттер для перевірки, чи є ще історії для завантаження
  get hasMoreStories(): boolean {
    return !this.allStoriesLoaded;
  }

  // Геттери для історій за фасетом
  get isLoadingFacetStories(): boolean {
    return this.isFacetStoriesFetching;
  }

  get hasMoreFacetStories(): boolean {
    return !this.allFacetStoriesLoaded;
  }

  getInitialFriends() {
    this.subscriptions.add(
      this.friendsCrudService
        .getFriends('', 50)
        .pipe(
          tap(() => (this.isFriendsFetching = true)),
          finalize(() => (this.isFriendsFetching = false)),
        )
        .subscribe((friends: IFriendsResponse) => {
          if (friends.data.length) {
            this.friendsList.set(friends.data);
            this.allFriendsLoaded = friends.total <= this.friendsList().length;
            this.friendsTotal.set(friends.total);
            this.friendsCrudService.setFriendsTotal(friends.total);
          }
        }),
    );
  }

  onFriendsScroll(searchValue: string, bottomReached: boolean) {
    if (bottomReached && !this.allFriendsLoaded && !this.isFriendsFetching) {
      this.loadMoreFriends(searchValue || '');
    }
  }

  private loadMoreFriends(query: string): void {
    if (this.allFriendsLoaded || this.isFriendsFetching) return;

    this.isFriendsFetching = true;

    this.friendsCrudService
      .getFriends(query, 50, this.friendsList().length)
      .pipe(
        exhaustMap((friendsResponse) => {
          if (friendsResponse.data.length) {
            this.friendsList.update((list) => [...list, ...friendsResponse.data]);
            this.allFriendsLoaded = friendsResponse.total <= this.friendsList().length;
            this.friendsTotal.set(friendsResponse.total);
            this.friendsCrudService.setFriendsTotal(friendsResponse.total);
          }
          return of(null);
        }),
        finalize(() => (this.isFriendsFetching = false)),
      )
      .subscribe();
  }

  // Методи для отримання поточних значень
  getCurrentFriend(): IFriend | null {
    return this.currentFriendSubject.value;
  }

  getCurrentFriendId(): number | null {
    return this.currentFriendIdSubject.value;
  }

  getCurrentChapter(): FacetModel | null {
    return this.currentFriendChapterSubject.value;
  }

  getCurrentFacet(): FacetModel | null {
    return this.currentFriendFacetSubject.value;
  }

  // Очищення підписок при знищенні сервісу
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
